import type { AuthResponse, UserDto } from '@gl/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { FullScreenLoader } from '@/components/FullScreenLoader';
import { api, UNAUTHORIZED_EVENT } from './api';
import { queryKeys } from './query';
import { clearStoredToken, getStoredToken, setStoredToken } from './token';

interface AuthContextValue {
  token: string | null;
  user: UserDto | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Guarda el token y el usuario (después de login/registro/demo). */
  setSession: (session: AuthResponse) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(() => getStoredToken());

  const meQuery = useQuery({
    queryKey: queryKeys.me,
    queryFn: () => api.auth.me(),
    enabled: !!token,
    staleTime: 5 * 60_000,
  });

  const logout = useCallback(() => {
    clearStoredToken();
    setToken(null);
    queryClient.clear();
  }, [queryClient]);

  const setSession = useCallback(
    (session: AuthResponse) => {
      queryClient.clear();
      setStoredToken(session.token);
      queryClient.setQueryData(queryKeys.me, session.user);
      setToken(session.token);
    },
    [queryClient],
  );

  useEffect(() => {
    const onUnauthorized = () => logout();
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      user: token ? (meQuery.data ?? null) : null,
      isLoading: !!token && meQuery.isPending,
      isAuthenticated: !!token,
      setSession,
      logout,
    }),
    [token, meQuery.data, meQuery.isPending, setSession, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth tiene que usarse dentro de <AuthProvider>');
  return ctx;
}

/** Protege rutas: sin token → /login?next=<ruta actual>. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (isLoading) return <FullScreenLoader />;
  return <>{children}</>;
}

/** Para /login y /registro: si ya hay sesión, va al inicio. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}
